import {
	createContext,
	lazy,
	Suspense,
	useCallback,
	useContext,
	useMemo,
	useState,
	type PropsWithChildren,
} from 'react';

export type OpenPropertyDetailsOptions = {
	aiAnalyzed?: boolean;
};

type PropertyDetailsSelection = OpenPropertyDetailsOptions & {
	propertyId: number;
};

type PropertyDetailsModalActions = {
	openPropertyDetails: (
		propertyId: number,
		options?: OpenPropertyDetailsOptions,
	) => void;
	closePropertyDetails: VoidFunction;
};

const PropertyDetailsModalContext =
	createContext<PropertyDetailsModalActions | null>(null);

const PropertyDetailsModal = lazy(() =>
	import('./PropertyDetailsModal').then(module => ({
		default: module.PropertyDetailsModal,
	})),
);

export const PropertyDetailsModalProvider = ({
	children,
}: PropsWithChildren) => {
	const [selection, setSelection] = useState<PropertyDetailsSelection | null>(
		null,
	);

	const openPropertyDetails = useCallback(
		(propertyId: number, options: OpenPropertyDetailsOptions = {}) => {
			if (!Number.isInteger(propertyId) || propertyId <= 0) {
				return;
			}
			setSelection({ propertyId, ...options });
		},
		[],
	);

	const closePropertyDetails = useCallback(() => setSelection(null), []);
	const actions = useMemo(
		() => ({ openPropertyDetails, closePropertyDetails }),
		[closePropertyDetails, openPropertyDetails],
	);

	return (
		<PropertyDetailsModalContext.Provider value={actions}>
			{children}
			{selection ? (
				<Suspense fallback={null}>
					<PropertyDetailsModal
						propertyId={selection.propertyId}
						onClose={closePropertyDetails}
						aiAnalyzed={selection.aiAnalyzed}
					/>
				</Suspense>
			) : null}
		</PropertyDetailsModalContext.Provider>
	);
};

export const usePropertyDetailsModal = () => {
	const context = useContext(PropertyDetailsModalContext);
	if (!context) {
		throw new Error(
			'usePropertyDetailsModal must be used within PropertyDetailsModalProvider.',
		);
	}
	return context;
};
